// src/pages/Account.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useApp } from '../AppContext';
import { Card, SectionTitle, PageHeader, Btn, Field, Input, Flash, Badge } from '../components/UI';

export default function Account() {
  const { user, site, refresh } = useApp();
  const [flash, setFlash] = useState(null);
  const [pw, setPw]       = useState({ current: '', new: '', confirm: '' });
  const [pin, setPin]     = useState({ pin: '', confirm: '' });
  const perms = user?.permissions || {};

  function showFlash(type, msg) { setFlash({ type, msg }); setTimeout(() => setFlash(null), 4000); }

  async function changePassword() {
    if (pw.new !== pw.confirm) { showFlash('error', 'Passwords do not match.'); return; }
    if (pw.new.length < 8) { showFlash('error', 'Password must be at least 8 characters.'); return; }
    const r = await api.post('/api/account/change-password', { current_password: pw.current, new_password: pw.new, confirm_password: pw.confirm });
    if (r?.ok) { showFlash('success', 'Password updated.'); setPw({ current: '', new: '', confirm: '' }); }
    else showFlash('error', r?.data?.error || 'Failed.');
  }

  async function setUserPin() {
    if (pin.pin !== pin.confirm) { showFlash('error', 'PINs do not match.'); return; }
    const r = await api.post('/api/account/set-pin', { pin: pin.pin, confirm_pin: pin.confirm });
    if (r?.ok) { showFlash('success', 'PIN set.'); setPin({ pin: '', confirm: '' }); refresh(); }
    else showFlash('error', r?.data?.error || 'Failed.');
  }

  async function clearPin() {
    if (!window.confirm('Remove your PIN?')) return;
    const r = await api.post('/api/account/clear-pin', {});
    if (r?.ok) { showFlash('success', 'PIN removed.'); refresh(); }
  }

  const isAdmin = ['admin', 'superadmin'].includes(user?.role);
  const isEmployee = user?.role === 'employee';
  const canManageSecurity = !isEmployee; // Employees cannot change password, PIN, or 2FA

  return (
    <div style={{ padding: '20px 16px', maxWidth: 640, margin: '0 auto' }}>
      <PageHeader title="Account Settings" />
      {flash && <Flash type={flash.type}>{flash.msg}</Flash>}

      {/* Info */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>Account Info</SectionTitle>
        <div style={{ padding: 16 }}>
          {[
            ['Username', user?.username],
            ['Role', user?.role],
            ['2FA', user?.totp_enabled ? 'Enabled' : 'Not set up'],
          ].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
              <span style={{ color: 'var(--fg2)' }}>{label}</span>
              <span style={{ fontWeight: 'bold' }}>{val}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Permissions */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>Permissions</SectionTitle>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {Object.entries(site?.features ? {} : {}).length === 0 && Object.entries(perms).map(([key, val]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: val ? 'color-mix(in srgb, var(--accent) 8%, var(--bg2))' : 'var(--bg2)', border: '1px solid var(--border)', fontSize: 12 }}>
              <span style={{ color: val ? 'var(--accent)' : 'var(--fg4)', fontWeight: 'bold', width: 12 }}>{val ? '+' : '-'}</span>
              <span style={{ color: 'var(--fg2)' }}>{key.replace(/_/g, ' ').replace(/^can /, '')}</span>
            </div>
          ))}
        </div>
      </Card>

      {isEmployee && (
        <div style={{ padding: '10px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--fg3)', marginBottom: 16 }}>
          Password, PIN, and 2FA settings are managed by a manager or admin. Contact your manager if you need access changes.
        </div>
      )}

      {/* Change password — managers and above only */}
      {canManageSecurity && <Card style={{ marginBottom: 16 }}>
        <SectionTitle>Change Password</SectionTitle>
        <div style={{ padding: 16 }}>
          <Field label="Current Password"><Input type="password" value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} autoComplete="current-password" /></Field>
          <Field label="New Password" hint="Minimum 8 characters"><Input type="password" value={pw.new} onChange={e => setPw(p => ({ ...p, new: e.target.value }))} autoComplete="new-password" /></Field>
          <Field label="Confirm New Password"><Input type="password" value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} autoComplete="new-password" /></Field>
          <Btn onClick={changePassword}>Update Password</Btn>
        </div>
      </Card>}

      {/* PIN */}
      {canManageSecurity && !isAdmin && (
        <Card style={{ marginBottom: 16 }}>
          <SectionTitle>PIN Login</SectionTitle>
          <div style={{ padding: 16 }}>
            <p style={{ fontSize: 13, color: 'var(--fg2)', marginBottom: 16 }}>A 4-digit PIN lets you sign in quickly without typing your full password.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <Field label={`${user?.pin_hash ? 'New' : ''} PIN (4 digits)`}>
                <Input type="password" maxLength={4} inputMode="numeric" value={pin.pin} onChange={e => setPin(p => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))} style={{ textAlign: 'center', letterSpacing: 8, fontSize: 20 }} />
              </Field>
              <Field label="Confirm PIN">
                <Input type="password" maxLength={4} inputMode="numeric" value={pin.confirm} onChange={e => setPin(p => ({ ...p, confirm: e.target.value.replace(/\D/g, '').slice(0, 4) }))} style={{ textAlign: 'center', letterSpacing: 8, fontSize: 20 }} />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn onClick={setUserPin}>{user?.pin_hash ? 'Update PIN' : 'Set PIN'}</Btn>
              {user?.pin_hash && <Btn variant="danger" onClick={clearPin}>Remove PIN</Btn>}
            </div>
          </div>
        </Card>
      )}

      {/* 2FA — managers and above only */}
      {canManageSecurity && <Card>
        <SectionTitle>Two-Factor Authentication</SectionTitle>
        <div style={{ padding: 16 }}>
          <p style={{ fontSize: 13, color: 'var(--fg2)', marginBottom: 16 }}>
            {user?.totp_enabled
              ? '2FA is active on your account. You can manage your setup below.'
              : <>2FA adds an extra layer of security using an authenticator app.{perms.requires_2fa && <strong style={{ color: 'var(--warning)' }}> Your role requires 2FA.</strong>}</>
            }
          </p>
          <Link to="/2fa/setup" style={{ display: 'inline-block', padding: '8px 16px', background: 'var(--accent)', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: 13, borderRadius: 'var(--radius)' }}>
            {user?.totp_enabled ? 'Manage 2FA' : 'Set Up 2FA'}
          </Link>
        </div>
      </Card>}
    </div>
  );
}
